import {
    Vector3,
    MathUtils,
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    AmbientLight,
    DirectionalLight
} from 'three';

import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

const homePosition = new Vector3(0, 0, 0);
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/`;
}

function getCookie(name) {
    const cname = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i].trim();
        if (c.indexOf(cname) === 0) return c.substring(cname.length);
    }
    return "";
}

let darkmodeOn = parseInt(getCookie("darkmodeOn")) || 0;
if (darkmodeOn === 1) document.body.classList.add('dark-mode');

function createGlobe(containerId, modelPath, onClickCallback, options = {}) {
    const container = document.getElementById(containerId);
    const { initialScale = 3, cameraY = 2, ZoomScale = 0.3} = options;
    if (!container) return;

    container.setAttribute('alt', containerId);

    let model;
    let isHolding = false;
    let lastX = 0, lastY = 0;
    let wasDragging = false;
    let targetScale = initialScale;

    const scene = new Scene();
    const camera = new PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 100);
    camera.position.set(0, cameraY, 7);

    const renderer = new WebGLRenderer({
        alpha: true,
        antialias: !isMobile,
        powerPreference: isMobile ? "low-power" : "high-performance"
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(renderer.domElement);

    scene.add(new AmbientLight(0xffffff, 0.5));
    const dirLight = new DirectionalLight(0xffffff, 0.5);
    dirLight.position.set(1, 1, 1);
    scene.add(dirLight);

    function onDrag(x, y) {
        if (!isHolding || !model) return;
        wasDragging = true;
        const deltaX = x - lastX;
        const deltaY = y - lastY;
        model.rotation.y += deltaX * 0.01;
        model.rotation.x += deltaY * 0.01;
        model.position.x += deltaX * 0.002;
        model.position.y -= deltaY * 0.002;
        model.position.x = MathUtils.clamp(model.position.x, -1.2, 1.2);
        model.position.y = MathUtils.clamp(model.position.y, -1.0, 1.0);
        lastX = x;
        lastY = y;
    }

    function loadModel() {
        const loader = new GLTFLoader();
        loader.setMeshoptDecoder(MeshoptDecoder);
        loader.load(
            modelPath,
            (gltf) => {
                model = gltf.scene;
                model.scale.set(2, 2, 2);
                model.position.copy(homePosition);
                scene.add(model);
            },
            undefined,
            (error) => console.error(`Error loading model for ${containerId}:`, error)
        );
    }

    function animate() {
        requestAnimationFrame(animate);
        if (model && !isHolding) {
            model.position.lerp(homePosition, 0.08);
            model.rotation.y += 0.025;
        }
        if (model) {
            model.scale.lerp(new Vector3(targetScale, targetScale, targetScale), 0.1);
        }
        renderer.render(scene, camera);
    }
    animate();

    if ('requestIdleCallback' in window) {
        requestIdleCallback(loadModel, { timeout: 1000 });
    } else {
        setTimeout(loadModel, 100);
    }

    container.addEventListener("mouseenter", () => {
        if (!isMobile) { targetScale = initialScale + ZoomScale ; document.body.style.cursor = 'pointer'; }
    });
    container.addEventListener("mouseleave", () => {
        if (!isMobile) { targetScale = initialScale; document.body.style.cursor = 'auto'; }
    });
    container.addEventListener("mousedown", (e) => {
        isHolding = true; wasDragging = false; lastX = e.clientX; lastY = e.clientY;
    });
    document.addEventListener("mousemove", (e) => onDrag(e.clientX, e.clientY));
    document.addEventListener("mouseup", () => { isHolding = false; });
    container.addEventListener("click", () => {
        if (isMobile || wasDragging) { wasDragging = false; return; }
        if (onClickCallback) onClickCallback(targetScale, (s) => { targetScale = s; });
    });

    container.addEventListener("touchstart", (e) => {
        if (e.touches.length > 1) return;
        e.preventDefault();
        isHolding = true; wasDragging = false;
        const touch = e.touches[0];
        lastX = touch.clientX; lastY = touch.clientY;
        targetScale = initialScale + ZoomScale;
    }, { passive: false });
    container.addEventListener("touchmove", (e) => {
        if (e.touches.length > 1) return;
        e.preventDefault();
        onDrag(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: false });
    container.addEventListener("touchend", (e) => {
        e.preventDefault();
        isHolding = false;
        if (!wasDragging && onClickCallback) {
            onClickCallback(targetScale, (s) => { targetScale = s; });
        } else {
            targetScale = initialScale;
        }
        wasDragging = false;
    }, { passive: false });

    window.addEventListener('resize', () => {
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
    });
}

function toggleDarkMode(targetScale, setTargetScale) {
    darkmodeOn = darkmodeOn === 0 ? 1 : 0;
    document.body.classList.toggle('dark-mode');
    setTargetScale(2.4);
    setTimeout(() => setTargetScale(3.3), 200);
    setCookie("darkmodeOn", darkmodeOn, 30);
}

let upsideDown = false;


function toggleUpsideDown(targetScale, setTargetScale, globeOptions) {
    const { initialScale = 1, ZoomScale = 0.1 } = globeOptions;

    upsideDown = !upsideDown;
    document.body.style.transition = "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)";
    document.body.style.transform = upsideDown ? "rotate(180deg)" : "";

    setTargetScale(initialScale);
    setTimeout(() => setTargetScale(initialScale + ZoomScale), 200);
}
document.addEventListener("DOMContentLoaded", () => {
    // Globe 1 — dark mode toggle
    createGlobe("globe", "/assets/home/glb/hot_chocolate.glb", toggleDarkMode);

    // Globe 2 — bunny upside down
    createGlobe(
    "globe2",
    "/assets/home/glb/choco_bunny.glb",
    (targetScale, setTargetScale) => toggleUpsideDown(targetScale, setTargetScale, { initialScale: 1, ZoomScale: 0.1 }),
    { initialScale: 1, cameraY: 0.8, ZoomScale: 0.1 }
);
});